import { useCallback } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/redux/hooks';
import { getBidderId } from '@/redux/modules/account/user/user.selectors';
import { getCatalogRegistration } from '@/redux/modules/catalog/registration/catalogRegistration.selectors';
import { getUrlFromPathname } from '@/redux/modules/config';
import {
    getItemSummaryBidderHasBid,
    getItemSummaryBidderHasHighBid,
    getItemSummaryIsReserveMet,
} from '@/redux/modules/item/summary/itemSummary.selectors';
import { getItemFacets } from '@/redux/modules/itemFacets';
import { TrackBidPlacedPrebidStatus } from '@/utils/amplitude/analytics/bidding/biddingAnalytics.types';
import { getUserPrebidStatus } from '@/utils/amplitude/analytics/bidding/biddingAnalytics.utils';
import { track } from '@/utils/amplitude/analytics/core';
import {
    AnalyticsCatalogRegistrationStatus,
    TrackingEvents,
    TrackingPayload,
} from '@/utils/amplitude/analytics/core.types';
import { getAnalyticsRegistrationStatus } from '@/utils/catalog/getAnalyticsRegistrationStatus';
import { getAmplitudePageNameFromPath } from '@/utils/getPageNameFromPath';

export type TrackPlaceBidPressedButtonType = 'Place Bid Button' | 'Button 1' | 'Button 2' | 'Button 3';

export enum TrackPlaceBidPressedButtonLocation {
    BidPanel = 'Bid Panel',
    LiveConsole = 'Live Console',
    ItemCard = 'Item Card',
    StickyBidPanel = 'Sticky Bid Panel',
}

type TrackPlaceBidPressedPayload = TrackingPayload & {
    auctionRegistrationStatus: AnalyticsCatalogRegistrationStatus;
    buttonLocation?: TrackPlaceBidPressedButtonLocation;
    buttonType?: TrackPlaceBidPressedButtonType;
    isLoggedIn: boolean;
    itemId: number;
    lotCategoryLvl1: string;
    lotCategoryLvl2: string;
    userPrebidStatus: TrackBidPlacedPrebidStatus;
};

export const trackPlaceBidPressedAnalytics = (payload: TrackPlaceBidPressedPayload) => {
    track(TrackingEvents.Bidding.PlaceBidPressed, payload);
};

export const useTrackPlaceBidPressedAnalytics = (catalogId: number, itemId: number) => {
    const { pathname } = useLocation();
    const url = useAppSelector((state) => getUrlFromPathname(state, pathname));
    const bidderId = useAppSelector(getBidderId);
    const catalogRegistration = useAppSelector((state) => getCatalogRegistration(state, catalogId));
    const { categories } = useAppSelector((state) => getItemFacets(state, itemId));
    const bidderHasBid = useAppSelector((state) => getItemSummaryBidderHasBid(state, itemId));
    const bidderHasHighBid = useAppSelector((state) => getItemSummaryBidderHasHighBid(state, itemId));
    const isReserveMet = useAppSelector((state) => getItemSummaryIsReserveMet(state, itemId));

    return useCallback(
        // For tracking purposes, we default to 'Place Bid Button' if no button type is provided otherwise we'd get NONE in the amplitude event filtering/tracking
        (
            buttonLocation: TrackPlaceBidPressedButtonLocation = TrackPlaceBidPressedButtonLocation.BidPanel,
            buttonType: TrackPlaceBidPressedButtonType = 'Place Bid Button'
        ) => {
            trackPlaceBidPressedAnalytics({
                auctionRegistrationStatus: getAnalyticsRegistrationStatus(catalogRegistration),
                buttonLocation,
                buttonType,
                isLoggedIn: bidderId > 0,
                itemId,
                lotCategoryLvl1: categories?.[0]?.l1CategoryLabel ?? 'Unknown',
                lotCategoryLvl2: categories?.[0]?.l2CategoryLabel ?? 'Unknown',
                pageName: getAmplitudePageNameFromPath(pathname),
                url,
                userPrebidStatus: getUserPrebidStatus(bidderHasBid, bidderHasHighBid, isReserveMet),
            });
        },
        [bidderHasBid, bidderHasHighBid, bidderId, catalogRegistration, categories, isReserveMet, itemId, pathname, url]
    );
};
